/**
 * Floating active call bar — shows at the bottom of the screen on any page
 * when the agent is on a call. Provides duration, mute, and hangup controls.
 * Shows caller/lead phone number.
 *
 * Supports minimize: collapses to a small floating pill in the bottom-right
 * corner. Click the pill to restore the full bar.
 */

import { Phone, PhoneOff, Mic, MicOff, User, Minimize2, Maximize2, FileText, WifiOff } from 'lucide-react'
import type { DeviceStatus } from '../../hooks/useVoIPDevice'

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

interface Props {
  status: DeviceStatus
  duration: number
  isMuted: boolean
  onToggleMute: () => void
  onHangup: () => void
  callNumber: string | null
  isMinimized: boolean
  onToggleMinimize: () => void
  onOpenNotes?: () => void
  isNotesPanelOpen?: boolean
  connectionUnstable?: boolean
}

export default function ActiveCallBar({ status, duration, isMuted, onToggleMute, onHangup, callNumber, isMinimized, onToggleMinimize, onOpenNotes, isNotesPanelOpen, connectionUnstable }: Props) {
  if (status !== 'on-call' && status !== 'connecting' && status !== 'ringing-inbound') {
    return null
  }

  const isConnecting = status === 'connecting' || status === 'ringing-inbound'
  const barColor = isConnecting ? 'bg-amber-500' : 'bg-green-600'

  // Minimized: small floating pill in bottom-right
  if (isMinimized) {
    return (
      <button
        onClick={onToggleMinimize}
        className={`fixed bottom-4 right-4 z-[90] ${barColor} text-white shadow-lg rounded-full px-4 py-2.5 flex items-center gap-2.5 hover:scale-105 transition-transform cursor-pointer`}
        title="Expand call bar"
      >
        <Phone size={14} className={isConnecting ? 'animate-pulse' : ''} />
        <span className="text-sm font-semibold font-mono">
          {isConnecting ? '...' : formatDuration(duration)}
        </span>
        <Maximize2 size={12} className="text-white/70" />
      </button>
    )
  }

  return (
    <div className={`fixed bottom-0 left-0 right-0 z-[90] ${barColor} text-white shadow-lg`}>
      <div className="max-w-screen-xl mx-auto px-4 py-2.5 flex items-center justify-between">
        {/* Left: status + caller info */}
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <Phone size={16} className={isConnecting ? 'animate-pulse' : ''} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold">
                {isConnecting ? 'Connecting...' : `On Call — ${formatDuration(duration)}`}
              </p>
            </div>
            {connectionUnstable && (
              <p className="text-xs text-amber-200 flex items-center gap-1 animate-pulse">
                <WifiOff size={10} />
                Connection unstable...
              </p>
            )}
            {callNumber && (
              <p className="text-xs text-white/70 flex items-center gap-1">
                <User size={10} />
                {callNumber}
              </p>
            )}
          </div>
        </div>

        {/* Right: controls */}
        <div className="flex items-center gap-2">
          {status === 'on-call' && (
            <>
              <button
                onClick={onToggleMute}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  isMuted
                    ? 'bg-red-500/30 hover:bg-red-500/50 text-white'
                    : 'bg-white/20 hover:bg-white/30 text-white'
                }`}
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? <MicOff size={15} /> : <Mic size={15} />}
                {isMuted ? 'Unmute' : 'Mute'}
              </button>
              {onOpenNotes && (
                <button
                  onClick={onOpenNotes}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    isNotesPanelOpen
                      ? 'bg-white/30 text-white'
                      : 'bg-white/20 hover:bg-white/30 text-white'
                  }`}
                  title="Call Notes"
                >
                  <FileText size={15} />
                  Notes
                </button>
              )}
            </>
          )}
          <button
            onClick={onHangup}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors"
          >
            <PhoneOff size={15} />
            Hang Up
          </button>
          <button
            onClick={onToggleMinimize}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-sm transition-colors"
            title="Minimize call bar"
          >
            <Minimize2 size={14} />
            <span className="text-white/70 text-xs hidden sm:inline">Min</span>
          </button>
        </div>
      </div>
    </div>
  )
}
